import {Redirect, Route} from 'react-router-dom';
import {
    IonAlert,
    IonApp, IonRouterOutlet, setupIonicReact
} from '@ionic/react';
import {IonReactHashRouter} from '@ionic/react-router';
import Users from './pages/Users';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

import './theme/main.scss'

import Page from "./pages/Page";
import LoginRequired from './components/LoginRequired';
import Profile from "./pages/Profile";
import ViewUser from "./pages/ViewUser";
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState} from "./store";
import {AdminIndex} from "./pages/admin/AdminIndex";
import AdminPage from "./pages/admin/AdminPage";
import {AdminUsers} from "./pages/admin/AdminUsers";
import {AdminViewUser} from "./pages/admin/AdminViewUser";
import {AdminUserManagedAccesses} from "./pages/admin/AdminUserManagedAccesses";
import {AdminUserUserNames} from "./pages/admin/AdminUserUserNames";
import {AdminServiceTypes} from "./pages/admin/AdminServiceTypes";
import {AdminViewServiceType} from "./pages/admin/AdminViewServiceType";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";

setupIonicReact();

const updateServiceWorker = (serviceWorkerRegistration: ServiceWorkerRegistration) => {
    const registrationWaiting = serviceWorkerRegistration.waiting;
    if (registrationWaiting) {
        registrationWaiting.postMessage({ type: 'SKIP_WAITING' });
        registrationWaiting.addEventListener('statechange', (e: any) => {
            if (e.target.state === 'activated') {
                window.location.reload();
            }
        });
    }
};

const queryClient = new QueryClient()

const App: React.FC = () => {
    const isServiceWorkerUpdated = useSelector<RootState>((state) => state.worker.serviceWorkerUpdated);
    const serviceWorkerRegistration = useSelector<RootState>((state) => state.worker.serviceWorkerRegistration);
    const [showAlert, setShowAlert] = useState(false);
    useEffect(() => {
        if (isServiceWorkerUpdated) {
            setShowAlert(true);
        }
    }, [isServiceWorkerUpdated]);

    return (
        <QueryClientProvider client={queryClient}>
            <IonApp>
                <IonAlert
                    isOpen={showAlert}
                    onDidDismiss={() => setShowAlert(false)}
                    header={'App Update Available'}
                    message={`An updated version of this app is available. Update now?`}
                    buttons={[
                        {
                            text: 'No',
                            role: 'cancel',
                            cssClass: 'secondary',
                            handler: () => {
                                setShowAlert(false);
                            }
                        },
                        {
                            text: 'Yes',
                            handler: () => {
                                setShowAlert(false);
                                updateServiceWorker(serviceWorkerRegistration as ServiceWorkerRegistration);
                            }
                        }
                    ]}
                />
                <LoginRequired>
                    <IonReactHashRouter>
                            <IonRouterOutlet>
                                <Route exact path="/admin/users/:id/userNames" render={(props) => {
                                    return <AdminPage title="Admin - User Names" backButtonUrl={`/admin/users/${props.match.params.id}`}>
                                        <AdminUserUserNames userId={+props.match.params.id} />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/admin/users/:id/managedAccesses" render={(props) => {
                                    return <AdminPage title="Admin - User Accesses" backButtonUrl={`/admin/users/${props.match.params.id}`}>
                                        <AdminUserManagedAccesses userId={+props.match.params.id} />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/admin/users/:id" render={(props) => {
                                    return <AdminPage title="Admin - View User" backButtonUrl={"/admin/users"}>
                                        <AdminViewUser userId={+props.match.params.id} />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/admin/serviceTypes/:id" render={(props) => {
                                    return <AdminPage title="Admin - View Service Type" backButtonUrl={"/admin/serviceTypes"}>
                                        <AdminViewServiceType serviceTypeId={+props.match.params.id} />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/admin/serviceTypes" render={(props) => {
                                    return <AdminPage title="Admin - Service Types">
                                        <AdminServiceTypes />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/admin/users" render={(props) => {
                                    return <AdminPage title="Admin - Users">
                                        <AdminUsers />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/admin" render={(props) => {
                                    return <AdminPage title="Admin dashboard">
                                        <AdminIndex />
                                    </AdminPage>
                                }}>
                                </Route>
                                <Route exact path="/profile" render={(props) => {
                                    return <Page title="Profile" backButtonUrl={"/"}>
                                        <Profile />
                                    </Page>
                                }}>
                                </Route>
                                <Route exact path="/users" render={(props) => {
                                    return <Page title="Users">
                                        <Users />
                                    </Page>
                                }}>
                                </Route>
                                <Route exact path="/users/:id" render={(props) => {
                                    return <Page backButtonUrl="/" title="View User">
                                        <ViewUser userId={+props.match.params.id} />
                                    </Page>;
                                }}>
                                </Route>
                                <Route exact path="/">
                                    <Redirect to="/users"/>
                                </Route>
                            </IonRouterOutlet>
                    </IonReactHashRouter>
                </LoginRequired>
            </IonApp>
            <ReactQueryDevtools/>
        </QueryClientProvider>
    );
}

export default App;
